const AUTH_COOKIE_MAX_AGE = 60 * 60 * 24 * 30;
const AUTH_COOKIE_CHUNK_SIZE = 3000;

function resolveSupabaseProjectRef() {
  try {
    // Must be a static `process.env.X` member expression: requireEnv() reads
    // `process.env[name]` dynamically, which Next/Turbopack cannot inline into
    // the client bundle. It therefore threw in the browser and this silently
    // fell back to 'menuraq' , so the exported key named a cookie that has
    // never existed, while supabase-js used the real project ref for storage.
    const url = process.env.NEXT_PUBLIC_SUPABASE_DB_URL;
    if (!url) return 'menuraq';
    return new URL(url).hostname.split('.')[0] || 'menuraq';
  } catch {
    return 'menuraq';
  }
}

export const SUPABASE_AUTH_STORAGE_KEY = `sb-${resolveSupabaseProjectRef()}-auth-token`;

/**
 * True when *some* Supabase auth token is present in cookies.
 *
 * Deliberately matches on the `sb-<ref>-auth-token` shape rather than trusting
 * SUPABASE_AUTH_STORAGE_KEY, so a project-ref mismatch can never be mistaken
 * for "the user has no session" , that mistake logs everybody out.
 */
export function hasStoredSupabaseSession() {
  if (!isBrowser()) return false;
  return document.cookie.split('; ').some((entry) => {
    const separator = entry.indexOf('=');
    if (separator <= 0) return false;
    const name = entry.slice(0, separator);
    const value = entry.slice(separator + 1);
    if (!value) return false;
    return /^sb-.+-auth-token(\.\d+)?$/.test(name);
  });
}

function isBrowser() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

/**
 * No subdomain routing anymore , everything runs on the same origin.
 * We never need a shared Domain= attribute, so always return undefined.
 * (Setting Domain=localhost in some browsers actually restricts the cookie
 * rather than sharing it, so it's safer to omit it entirely.)
 */
function getCookieAttributes(maxAge = AUTH_COOKIE_MAX_AGE) {
  const secure = isBrowser() && window.location.protocol === 'https:';
  return [
    'Path=/',
    `Max-Age=${maxAge}`,
    'SameSite=Lax',
    secure ? 'Secure' : '',
  ].filter(Boolean).join('; ');
}

function readCookie(name: string) {
  if (!isBrowser()) return null;
  const needle = `${encodeURIComponent(name)}=`;
  const match = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(needle));
  return match ? decodeURIComponent(match.slice(needle.length)) : null;
}

function writeCookie(name: string, value: string) {
  if (!isBrowser()) return;
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; ${getCookieAttributes()}`;
}

function removeCookie(name: string) {
  if (!isBrowser()) return;
  // Expire with Path=/ (the only attribute needed for removal on same origin)
  document.cookie = `${encodeURIComponent(name)}=; Path=/; Max-Age=0; SameSite=Lax`;
}

function chunkName(key: string, index: number) {
  return `${key}.${index}`;
}

function clearChunks(key: string) {
  for (let index = 0; index < 20; index += 1) {
    removeCookie(chunkName(key, index));
  }
}

function readChunkedCookie(key: string) {
  const direct = readCookie(key);
  if (direct) return direct;

  const chunks: string[] = [];
  for (let index = 0; index < 20; index += 1) {
    const value = readCookie(chunkName(key, index));
    if (!value) break;
    chunks.push(value);
  }
  return chunks.length ? chunks.join('') : null;
}

function writeChunkedCookie(key: string, value: string) {
  removeCookie(key);
  clearChunks(key);

  if (value.length <= AUTH_COOKIE_CHUNK_SIZE) {
    writeCookie(key, value);
    return;
  }

  for (
    let offset = 0, index = 0;
    offset < value.length;
    offset += AUTH_COOKIE_CHUNK_SIZE, index += 1
  ) {
    writeCookie(chunkName(key, index), value.slice(offset, offset + AUTH_COOKIE_CHUNK_SIZE));
  }
}

export const sharedSupabaseAuthStorage = {
  getItem(key: string) {
    if (!isBrowser()) return null;
    const cookieValue = readChunkedCookie(key);
    if (cookieValue) return cookieValue;

    // Migrate any legacy localStorage value into the cookie on first read
    const legacyValue = window.localStorage.getItem(key);
    if (legacyValue) writeChunkedCookie(key, legacyValue);
    return legacyValue;
  },

  setItem(key: string, value: string) {
    if (!isBrowser()) return;
    writeChunkedCookie(key, value);
    // Do NOT write to localStorage , tokens should only live in cookies
    // to prevent XSS exfiltration via localStorage access.
  },

  removeItem(key: string) {
    if (!isBrowser()) return;
    removeCookie(key);
    clearChunks(key);
    window.localStorage.removeItem(key);
  },
};